import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Home as HomeIcon, Building2, Compass, User, MessageCircle, BadgeCheck, Camera } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useSiteSettings, buildWhatsAppUrl } from '../lib/settings';

const intentLabel = { sell: 'For Sale', rent: 'For Rent' };

const OwnerListingCard = ({ listing }) => {
  const { whatsapp_number } = useSiteSettings();
  const CatIcon = listing.listing_category === 'commercial' ? Building2 : HomeIcon;
  const wa = buildWhatsAppUrl(whatsapp_number, `Hi SafeHouse, I'm interested in the owner-listed property "${listing.property_name || listing.bhk + ' at ' + (listing.location || listing.city)}" (${listing.expected_price || ''})`);

  const initial = (listing.name || 'O').trim().charAt(0).toUpperCase();

  const hasImage = Array.isArray(listing.images) && listing.images.length > 0;
  const heroImg = hasImage ? listing.images[0] : null;

  return (
    <div className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col">
      {/* Header banner — photo or gradient */}
      <div className={`relative h-40 md:h-44 ${hasImage ? 'bg-gray-100' : 'bg-gradient-to-br from-[#9a1b2f] to-[#7a1524]'} text-white`}>
        {hasImage && (
          <>
            <img src={heroImg} alt={listing.property_name || 'Property'} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/40" />
          </>
        )}
        <div className="relative p-4 flex items-start justify-between h-full">
          <div className="flex items-center gap-2">
            <div className={`w-11 h-11 rounded-full ${hasImage ? 'bg-white/25 backdrop-blur-md' : 'bg-white/20 backdrop-blur-sm'} flex items-center justify-center font-black text-lg`}>
              {initial}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-90 font-semibold flex items-center gap-1">
                <BadgeCheck className="w-3 h-3" /> Owner Listed
              </div>
              <div className="font-bold text-sm truncate max-w-[140px]">{listing.name}</div>
            </div>
          </div>
          <Badge className="bg-white text-[#9a1b2f] hover:bg-white border-0 font-bold shadow-sm">{intentLabel[listing.intent] || 'Listing'}</Badge>
        </div>

        {/* Photo count badge */}
        {hasImage && listing.images.length > 1 && (
          <div className="absolute bottom-2 left-3 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px] font-semibold flex items-center gap-1">
            <Camera className="w-2.5 h-2.5" /> {listing.images.length} photos
          </div>
        )}

        {/* Decorative pattern (fallback only) */}
        {!hasImage && (
          <div className="pointer-events-none absolute inset-0 opacity-10">
            <div className="absolute -right-6 -bottom-6 w-28 h-28 rounded-full border-[6px] border-white" />
            <div className="absolute right-10 top-1 w-16 h-16 rounded-full border-[3px] border-white" />
          </div>
        )}
      </div>

      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-bold text-gray-900 text-base leading-tight line-clamp-1 group-hover:text-[#9a1b2f] transition-colors">
          {listing.property_name || `${listing.bhk} ${listing.property_type} in ${listing.location || listing.city}`}
        </h3>
        <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
          <MapPin className="w-3 h-3" /> <span className="line-clamp-1">{[listing.location, listing.city].filter(Boolean).join(', ')}</span>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-3">
          <Badge variant="outline" className="text-[10px] capitalize border-gray-200 flex items-center gap-1"><CatIcon className="w-2.5 h-2.5" /> {listing.listing_category}</Badge>
          <Badge variant="outline" className="text-[10px] capitalize border-gray-200">{listing.property_type}</Badge>
          {listing.bhk && <Badge variant="outline" className="text-[10px] border-gray-200">{listing.bhk}</Badge>}
        </div>

        <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-gray-100 text-xs">
          <div>
            <div className="text-[10px] uppercase text-gray-400 tracking-wide">Area</div>
            <div className="font-bold text-gray-900">{listing.sqft ? `${listing.sqft} sqft` : '—'}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase text-gray-400 tracking-wide">{listing.intent === 'rent' ? 'Rent' : 'Expected'}</div>
            <div className="font-black text-[#9a1b2f]">{listing.expected_price || 'On Request'}</div>
          </div>
        </div>

        {listing.message && (
          <p className="mt-3 text-xs text-gray-600 bg-gray-50 rounded-lg p-2 line-clamp-2">“{listing.message}”</p>
        )}

        <div className="grid grid-cols-2 gap-2 mt-auto pt-3">
          <Link to={`/owner-listings/${listing.id}`}>
            <Button size="sm" variant="outline" className="w-full h-9 text-xs font-semibold border-[#9a1b2f] text-[#9a1b2f] hover:bg-[#9a1b2f] hover:text-white">View Details</Button>
          </Link>
          <a href={wa} target="_blank" rel="noopener noreferrer">
            <Button size="sm" className="w-full h-9 text-xs font-semibold bg-[#25D366] hover:bg-[#20b955] text-white">
              <MessageCircle className="w-3 h-3 mr-1" /> WhatsApp
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default OwnerListingCard;
